/**
 * The single source of truth for the sales-goal / KPI metric catalog.
 *
 * Before this file five parallel lists described the same 14 metrics — the
 * crm-api goals module, the shared metric-preference DTOs, the goals module's
 * frontend types, the KPI Overview card list and the goals-table transform —
 * and they had already drifted: one omitted `dailyUniqueItems`, and the
 * goals-table used the key `monthlyGPMargin` where everything else said
 * `monthlyGrossProfitMargin`. Every consumer now derives its metric list,
 * order and label from here.
 *
 * Order matters: it is the order KPI Overview renders its cards, the order
 * the KPI Details (goals-table) report renders its columns, and the order the
 * Visible KPIs settings page lists its checkboxes.
 */

/** Which section of the goal-setting forms a metric belongs to. */
export type GoalMetricGroup = 'monthly' | 'daily';

export interface GoalMetricDefinitionModel {
	/** Canonical identifier. Matches the `goal` table column where one exists. */
	key: string;
	/** Short label used for report columns, KPI cards and settings checkboxes. */
	label: string;
	/**
	 * Longer label used on the goal-setting forms, which show units.
	 *
	 * May contain `{{name}}` placeholders for values that are per tenant
	 * rather than fixed — today only the productive-call threshold
	 * (PD-14758). Render it with `t(metric.formLabel,
	 * getGoalMetricLabelParams(metric, context))` so the placeholder is
	 * filled; `t()` leaves an unfilled placeholder visible on purpose.
	 */
	formLabel: string;
	group: GoalMetricGroup;
	/** Render as a currency amount. */
	isCurrency: boolean;
	/** Render as a percentage. */
	isPercent: boolean;
	/**
	 * Gross-profit derived, so it is additionally gated by `orders:gp:view`
	 * (the `GpStripInterceptor` removes these keys from responses).
	 */
	isGp: boolean;
	/** Unit rendered after the value (e.g. "min"), when the metric has one. */
	unitSuffix?: string;
	/**
	 * Whether `goal` / `team_goal` / `company_goal` carry a column for this
	 * metric. `dailyUniqueItems` is reported as an actual on KPI Overview but
	 * has never had a goal column, so it does not appear on the goal forms or
	 * as a goals-table column.
	 */
	hasGoalValue: boolean;
}

export const GOAL_METRICS: ReadonlyArray<GoalMetricDefinitionModel> = [
	{
		key: 'monthlyRevenue',
		label: 'Revenue',
		formLabel: 'Monthly Revenue ($)',
		group: 'monthly',
		isCurrency: true,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'monthlyGP',
		label: 'Gross Profit',
		formLabel: 'Monthly Gross Profit ($)',
		group: 'monthly',
		isCurrency: true,
		isPercent: false,
		isGp: true,
		hasGoalValue: true,
	},
	{
		key: 'monthlyGrossProfitMargin',
		label: 'GP Margin %',
		formLabel: 'Monthly Gross Profit Margin (%)',
		group: 'monthly',
		isCurrency: false,
		isPercent: true,
		isGp: true,
		hasGoalValue: true,
	},
	{
		key: 'monthlyQuotes',
		label: 'Quotes',
		formLabel: 'Monthly Quotes',
		group: 'monthly',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'monthlyCustomersClosed',
		label: 'New Customers',
		formLabel: 'Monthly New Customers',
		group: 'monthly',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'monthlyCustomers',
		label: 'Customers',
		formLabel: 'Monthly Customers',
		group: 'monthly',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'dailyCustomers',
		label: 'Daily Customers',
		formLabel: 'Daily Customers',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'dailyLines',
		label: 'Lines',
		formLabel: 'Daily Lines',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'dailyUniqueItems',
		label: 'Unique Items',
		formLabel: 'Daily Unique Items',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: false,
	},
	{
		key: 'dailyCalls',
		label: 'Calls',
		formLabel: 'Daily Calls',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'dailyProductiveCalls',
		label: 'Productive Calls',
		formLabel: 'Daily Productive Calls (>= {{seconds}} sec)',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'dailyCallDuration',
		label: 'Call Duration',
		formLabel: 'Daily Call Duration (minutes)',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		unitSuffix: 'min',
		hasGoalValue: true,
	},
	{
		key: 'dailyLeadVisits',
		label: 'Lead Visits',
		formLabel: 'Daily Lead Visits',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
	{
		key: 'dailyCustomerVisits',
		label: 'Customer Visits',
		formLabel: 'Daily Customer Visits',
		group: 'daily',
		isCurrency: false,
		isPercent: false,
		isGp: false,
		hasGoalValue: true,
	},
];

/**
 * Canonical metric identifiers, in catalog order.
 *
 * Declared as a literal union rather than derived from `GOAL_METRICS` so it
 * can be used in a Zod enum and as an object key type. The
 * `goal-metrics.test.ts` lock-in asserts the two never diverge.
 */
export type GoalMetricName =
	| 'monthlyRevenue'
	| 'monthlyGP'
	| 'monthlyGrossProfitMargin'
	| 'monthlyQuotes'
	| 'monthlyCustomersClosed'
	| 'monthlyCustomers'
	| 'dailyCustomers'
	| 'dailyLines'
	| 'dailyUniqueItems'
	| 'dailyCalls'
	| 'dailyProductiveCalls'
	| 'dailyCallDuration'
	| 'dailyLeadVisits'
	| 'dailyCustomerVisits';

/** Every metric key, in catalog order. */
export const GOAL_METRIC_NAMES: ReadonlyArray<GoalMetricName> =
	GOAL_METRICS.map((metric) => metric.key as GoalMetricName);

/** Metrics that have a goal column, in catalog order. */
export const GOAL_METRICS_WITH_VALUES: ReadonlyArray<GoalMetricDefinitionModel> =
	GOAL_METRICS.filter((metric) => metric.hasGoalValue);

const GOAL_METRIC_KEY_SET = new Set<string>(GOAL_METRIC_NAMES);

/** Narrow an arbitrary string to a catalog metric key. */
export function isGoalMetricName(value: string): value is GoalMetricName {
	return GOAL_METRIC_KEY_SET.has(value);
}

/** Look up a metric definition by key. */
export function getGoalMetric(
	key: string,
): GoalMetricDefinitionModel | undefined {
	return GOAL_METRICS.find((metric) => metric.key === key);
}

/**
 * The tenant values a metric's `formLabel` template can refer to.
 *
 * Only the productive-call threshold needs one today, but the shape is a
 * context object so a second tenant-driven label never has to change every
 * call site again.
 */
export interface GoalMetricLabelContextModel {
	/** The tenant's productive-call threshold, in seconds (PD-14758). */
	productiveCallDurationSeconds: number;
}

/**
 * Interpolation params for a metric's `formLabel`, or `undefined` when the
 * label is a plain string. Feed the result straight to `t()` — passing
 * `undefined` as its second argument is the no-params call.
 */
export function getGoalMetricLabelParams(
	metric: GoalMetricDefinitionModel,
	context: GoalMetricLabelContextModel,
): Record<string, number> | undefined {
	if (metric.key === 'dailyProductiveCalls') {
		return { seconds: context.productiveCallDurationSeconds };
	}

	return undefined;
}

/**
 * Narrow a metric list to the tenant's visible KPIs, preserving catalog order.
 *
 * `visible == null` means the tenant has never configured the
 * `goals.visible_kpis` instance setting, which is defined as "everything is
 * visible" — so the input list comes back untouched. An EMPTY array is a
 * deliberate "hide everything" and returns nothing; that distinction is the
 * whole reason this takes a nullable second argument.
 */
export function filterVisibleKpis(
	metrics: ReadonlyArray<GoalMetricDefinitionModel>,
	visible: ReadonlyArray<string> | null | undefined,
): Array<GoalMetricDefinitionModel> {
	if (visible == null) {
		return [...metrics];
	}
	const allowed = new Set<string>(visible);
	return metrics.filter((metric) => allowed.has(metric.key));
}

/**
 * Resolve the stored `goals.visible_kpis` setting into the ordered list of
 * visible metric keys every server consumer works from.
 *
 * A missing setting row resolves to all 14 metrics (the documented default).
 * Keys that are not in the catalog are dropped, so a stale value left behind
 * by a renamed metric can never widen or narrow visibility unexpectedly.
 */
export function resolveVisibleGoalMetrics(
	setting: { metrics: ReadonlyArray<string> } | null | undefined,
): Array<GoalMetricName> {
	return filterVisibleKpis(GOAL_METRICS, setting?.metrics).map(
		(metric) => metric.key as GoalMetricName,
	);
}

/**
 * A per-role narrowing of the company's KPI list (PD-13518).
 *
 * A role that appears here sees ONLY these metrics. A role that is ABSENT is
 * unrestricted, which is the default and what every existing tenant has.
 */
export interface KpiRoleRestrictionModel {
	/** `_rbac_role._id`. */
	roleId: number;
	/**
	 * Metric keys this role may see. An empty list is a deliberate "this role
	 * sees no KPIs", the same way an empty company list hides everything.
	 */
	metrics: ReadonlyArray<string>;
}

/**
 * A restriction after normalization: the role still exists and the metric
 * list is a real, catalog-ordered subset of the company list.
 */
export interface NormalizedKpiRoleRestrictionModel {
	roleId: number;
	metrics: Array<GoalMetricName>;
}

/**
 * The stored shape of the `goals.visible_kpis` instance setting. `metrics` is
 * the company-wide list ("KPIs this company uses"); `roleRestrictions` narrows
 * it for individual roles.
 */
export interface VisibleKpisSettingModel {
	metrics: ReadonlyArray<string>;
	roleRestrictions?: ReadonlyArray<KpiRoleRestrictionModel>;
}

/**
 * One role a viewer holds, plus the role it extends (PD-15546) if any, so a
 * child role with no restriction of its own can fall back to its parent's.
 */
export interface KpiViewerRoleModel {
	roleId: number;
	parentRoleId?: number | null;
}

/**
 * Resolve the KPI metrics one viewer may see: the company list narrowed by the
 * restrictions on the roles they hold.
 *
 * The rules, in the order they bite:
 *
 * 1. The company list is the ceiling. A restriction can only take metrics
 *    away, never add one the company has switched off, so a stale key left in
 *    a role restriction is inert.
 * 2. Roles UNION, matching how RBAC actions combine — a user holding two
 *    restricted roles sees both roles' metrics.
 * 3. An unrestricted role is therefore a full-visibility grant: holding one
 *    means the company list, whatever the viewer's other roles say. This is
 *    what keeps an admin who is also a salesperson from losing cards.
 * 4. A child role with no restriction of its own inherits its parent's, which
 *    mirrors the read-time permission merge from PD-15546.
 * 5. A viewer with no roles at all is unrestricted. Nothing has been said
 *    about them, and inferring "see nothing" from silence would blank the KPI
 *    screens for API keys and background contexts.
 *
 * `GpStripInterceptor` still applies on top for the two GP metrics — a role
 * may be allowed `monthlyGP` here and still never receive it without
 * `orders:gp:view`.
 */
export function resolveEffectiveVisibleKpis(
	setting: VisibleKpisSettingModel | null | undefined,
	roles: ReadonlyArray<KpiViewerRoleModel>,
): Array<GoalMetricName> {
	const companyMetrics = resolveVisibleGoalMetrics(setting);

	const restrictionsByRoleId = new Map<number, Set<string>>();
	for (const restriction of setting?.roleRestrictions ?? []) {
		restrictionsByRoleId.set(
			restriction.roleId,
			new Set<string>(restriction.metrics),
		);
	}

	if (restrictionsByRoleId.size === 0 || roles.length === 0) {
		return companyMetrics;
	}

	const allowed = new Set<string>();

	for (const role of roles) {
		const ownRestriction = restrictionsByRoleId.get(role.roleId);
		const inheritedRestriction =
			role.parentRoleId == null
				? undefined
				: restrictionsByRoleId.get(role.parentRoleId);
		const restriction = ownRestriction ?? inheritedRestriction;

		// Rule 3 — one unrestricted role wins outright, so stop looking.
		if (restriction == null) {
			return companyMetrics;
		}

		for (const key of restriction) {
			allowed.add(key);
		}
	}

	return companyMetrics.filter((key) => allowed.has(key));
}

/**
 * The restrictions worth storing: rows whose role still exists and whose
 * metric list is a real subset of the company list.
 *
 * Called on the write path so the setting can never accumulate a restriction
 * that is either dead (a deleted role) or a no-op (every company metric
 * checked). A no-op row is dropped rather than stored because "restricted to
 * everything" and "unrestricted" behave identically, and keeping the shorter
 * form means the settings page shows a column only where a real narrowing
 * exists.
 */
export function normalizeKpiRoleRestrictions(
	companyMetrics: ReadonlyArray<string>,
	restrictions: ReadonlyArray<KpiRoleRestrictionModel> | null | undefined,
	knownRoleIds: ReadonlyArray<number>,
): Array<NormalizedKpiRoleRestrictionModel> {
	if (restrictions == null || restrictions.length === 0) {
		return [];
	}

	const companySet = new Set<string>(companyMetrics);
	const knownRoleIdSet = new Set<number>(knownRoleIds);
	const seenRoleIds = new Set<number>();
	const normalized: Array<NormalizedKpiRoleRestrictionModel> = [];

	for (const restriction of restrictions) {
		if (
			!knownRoleIdSet.has(restriction.roleId) ||
			seenRoleIds.has(restriction.roleId)
		) {
			continue;
		}
		seenRoleIds.add(restriction.roleId);

		// Intersect with the company list AND put it in catalog order, so the
		// stored value reads the same way every consumer will apply it.
		const metrics = GOAL_METRIC_NAMES.filter(
			(key) => companySet.has(key) && restriction.metrics.includes(key),
		);

		if (metrics.length === companySet.size) {
			continue;
		}

		normalized.push({ roleId: restriction.roleId, metrics });
	}

	return normalized;
}
